import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Pencil, Save, X, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    image: '',
    features: ['', '', '', '']
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/services`);
      setServices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: "Error",
        description: "Failed to load services",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingId(service.service_id);
    setEditData({ ...service });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (serviceId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/services/${serviceId}`, {
        title: editData.title,
        description: editData.description,
        image: editData.image,
        features: editData.features
      });

      toast({
        title: "Success",
        description: "Service updated successfully"
      });

      setEditingId(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        title: "Error",
        description: "Failed to update service",
        variant: "destructive"
      });
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...editData.features];
    newFeatures[index] = value;
    setEditData({ ...editData, features: newFeatures });
  };

  const handleAddService = async () => {
    try {
      const nextId = services.length > 0 ? Math.max(...services.map(s => s.service_id)) + 1 : 1;
      await axios.post(`${BACKEND_URL}/api/cms/services`, {
        service_id: nextId,
        ...newService,
        order: nextId
      });

      toast({
        title: "Success",
        description: "Service added successfully"
      });

      setShowAddForm(false);
      setNewService({ title: '', description: '', image: '', features: ['', '', '', ''] });
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
      toast({
        title: "Error",
        description: "Failed to add service",
        variant: "destructive"
      });
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cms/services/${serviceId}`);
      toast({
        title: "Success",
        description: "Service deleted successfully"
      });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: "Error",
        description: "Failed to delete service",
        variant: "destructive"
      });
    }
  };

  const handleNewFeatureChange = (index, value) => {
    const newFeatures = [...newService.features];
    newFeatures[index] = value;
    setNewService({ ...newService, features: newFeatures });
  };

  if (loading) {
    return <div className="text-center py-12">Loading services...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Services Management</h1>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Service
        </Button>
      </div>

      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-xl font-light text-[#2d2d2d] mb-4">Add New Service</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
              <Input
                value={newService.title}
                onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                placeholder="Service title"
              />
            </div>

            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Description</label>
              <Textarea
                value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                rows={3}
                placeholder="Service description"
              />
            </div>

            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Image URL</label>
              <Input
                value={newService.image}
                onChange={(e) => setNewService({ ...newService, image: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Features (4 required)</label>
              {newService.features.map((feature, index) => (
                <Input
                  key={index}
                  value={feature}
                  onChange={(e) => handleNewFeatureChange(index, e.target.value)}
                  className="mb-2"
                  placeholder={`Feature ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleAddService} className="bg-green-600 hover:bg-green-700 text-white">
                <Save className="w-4 h-4 mr-2" />
                Add Service
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {services.map((service) => (
          <div key={service.service_id} className="bg-white p-6 rounded-lg shadow-lg">
            {editingId === service.service_id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Description</label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Image URL</label>
                  <Input
                    value={editData.image}
                    onChange={(e) => setEditData({ ...editData, image: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Features (4 required)</label>
                  {editData.features?.map((feature, index) => (
                    <Input
                      key={index}
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      className="mb-2"
                      placeholder={`Feature ${index + 1}`}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleSave(service.service_id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-light text-[#2d2d2d] mb-2">{service.title}</h3>
                    <p className="text-[#2d2d2d] font-light mb-4">{service.description}</p>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                    <div className="space-y-2">
                      <p className="text-sm font-normal text-[#2d2d2d]">Features:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {service.features.map((feature, index) => (
                          <li key={index} className="text-[#2d2d2d] font-light">{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(service)}
                      className="bg-[#0A1A50] hover:bg-[#060F2E] text-white"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteService(service.service_id)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesManager;